/**
 * A creative's format, one rule for /creatives and the Channel Deep-dive's Ad
 * format and Creative tabs (spec 2026-10-15-native-creative-performance §3.4):
 * the platform's ad_format first, else a whole field of the ad name, else
 * unknown (null, "ไม่ระบุ"). Pure. It imports nothing from services/channels:
 * channelTypes imports this file's type, and a cycle would follow.
 *
 * @module services/creatives/creativeFormat
 */

export const CREATIVE_FORMATS = ['static', 'video', 'carousel', 'other'] as const;
export type CreativeFormat = (typeof CREATIVE_FORMATS)[number];

export const isCreativeFormat = (v: unknown): v is CreativeFormat =>
  typeof v === 'string' && (CREATIVE_FORMATS as readonly string[]).includes(v);

/**
 * The platform's value, upper-cased first: Google's ad types (RESPONSIVE_DISPLAY_AD, VIDEO_BUMPER_AD, …), TikTok's
 * SINGLE_VIDEO / SINGLE_IMAGE / CAROUSEL_ADS once the sync keeps them, and the Demo seeds' lower-case `image` / `text`.
 * Video is tested first: DEMAND_GEN_VIDEO_RESPONSIVE_AD is a video. Any other value is a format we do not group.
 */
const fromPlatform = (adFormat: string): CreativeFormat => {
  const v = adFormat.toUpperCase();
  if (v.includes('VIDEO')) return 'video';
  if (v.includes('CAROUSEL')) return 'carousel';
  if (v.includes('DISPLAY') || v.includes('IMAGE') || v.includes('MULTI_ASSET')) return 'static';
  return 'other';
};

/**
 * A name field that is exactly one of these (trimmed, lower-cased) decides. A Map, not an object literal, so a field
 * such as "constructor" never matches an inherited key. Photo Album is several images, so it counts as Carousel.
 */
const NAME_KEYWORDS: ReadonlyMap<string, CreativeFormat> = new Map<string, CreativeFormat>([
  ['static', 'static'], ['img', 'static'], ['image', 'static'],
  ['vdo', 'video'], ['video', 'video'],
  ['carousel', 'carousel'], ['photo album', 'carousel'],
  ['kols', 'other'],
]);

/** The first `|` field that is a whole keyword: "Ad01 | 151026 | Static | Summer" is static; "chubby-img" is nothing. */
const fromName = (adName: string): CreativeFormat | null =>
  adName.split('|')
    .map((field) => NAME_KEYWORDS.get(field.trim().toLowerCase()))
    .find((f): f is CreativeFormat => f !== undefined) ?? null;

/** The platform's value first (a blank one is none), else the name; null = unknown. */
export const creativeFormatOf = (adFormat: string | null, adName: string | null): CreativeFormat | null => {
  const platform = adFormat?.trim() ?? '';
  if (platform !== '') return fromPlatform(platform);
  return adName ? fromName(adName) : null;
};

type FormatT = (key: string) => string;

/** Literal keys, so the locale integrity test sees them. null = "ไม่ระบุ". */
export const creativeFormatLabel = (format: CreativeFormat | null, t: FormatT): string => {
  if (format === 'static') return t('creatives.format.static');
  if (format === 'video') return t('creatives.format.video');
  if (format === 'carousel') return t('creatives.format.carousel');
  if (format === 'other') return t('creatives.format.other');
  return t('creatives.format.none');
};
